import React from 'react';

const ProcessTimeline = () => {
  const steps = [
    {
      num: '01',
      title: 'Discovery & Architecture',
      subtitle: 'System Blueprinting & Feasibility',
      desc: 'In-depth tech stack selection, data schema design, infrastructure estimation, and technical feasibility blueprinting.',
      color: '#A855F7',
      bgColor: 'rgba(168, 85, 247, 0.12)',
      borderColor: 'rgba(168, 85, 247, 0.35)',
      glowColor: 'rgba(168, 85, 247, 0.2)',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
          <polyline points="14 2 14 8 20 8"></polyline>
          <line x1="16" y1="13" x2="8" y2="13"></line>
          <line x1="16" y1="17" x2="8" y2="17"></line>
        </svg>
      ),
      // Top Right 3D Cube Graphic
      graphic: (
        <svg width="100" height="80" viewBox="0 0 100 80" fill="none">
          {/* Stacked 3D Isometric Cubes */}
          <g opacity="0.85">
            {/* Top Cube */}
            <path d="M50 10 L70 20 L50 30 L30 20 Z" fill="#C084FC" />
            <path d="M30 20 L50 30 L50 42 L30 32 Z" fill="#9333EA" />
            <path d="M50 30 L70 20 L70 32 L50 42 Z" fill="#A855F7" />
            {/* Right Cube */}
            <path d="M72 25 L92 35 L72 45 L52 35 Z" fill="#A855F7" />
            <path d="M52 35 L72 45 L72 57 L52 47 Z" fill="#7E22CE" />
            <path d="M72 45 L92 35 L92 47 L72 57 Z" fill="#9333EA" />
            {/* Bottom Left Cube */}
            <path d="M35 32 L55 42 L35 52 L15 42 Z" fill="#E9D5FF" />
            <path d="M15 42 L35 52 L35 64 L15 54 Z" fill="#A855F7" />
            <path d="M35 52 L55 42 L55 54 L35 64 Z" fill="#C084FC" />
          </g>
        </svg>
      ),
      deliverables: ['System Blueprint', 'Tech Stack Plan', 'API Schemas'],
    },
    {
      num: '02',
      title: 'UI/UX & Design Systems',
      subtitle: 'Interactive Prototypes & UX',
      desc: 'High-fidelity wireframes, responsive design system guidelines, accessibility audits, and conversion-focused UX flows.',
      color: '#38BDF8',
      bgColor: 'rgba(56, 189, 248, 0.12)',
      borderColor: 'rgba(56, 189, 248, 0.35)',
      glowColor: 'rgba(56, 189, 248, 0.2)',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
          <line x1="3" y1="9" x2="21" y2="9"></line>
          <line x1="9" y1="21" x2="9" y2="9"></line>
        </svg>
      ),
      // Top Right Wireframe Graphic
      graphic: (
        <svg width="110" height="80" viewBox="0 0 110 80" fill="none">
          <g opacity="0.85">
            {/* Desktop Window Frame Wireframe */}
            <rect x="15" y="10" width="80" height="55" rx="6" stroke="#38BDF8" strokeWidth="1.5" fill="rgba(6, 182, 212, 0.1)" />
            <line x1="15" y1="22" x2="95" y2="22" stroke="#38BDF8" strokeWidth="1.5" />
            <circle cx="23" cy="16" r="2" fill="#38BDF8" />
            <circle cx="29" cy="16" r="2" fill="#38BDF8" />
            <circle cx="35" cy="16" r="2" fill="#38BDF8" />
            {/* Inner Grid Wireframe Elements */}
            <rect x="22" y="28" width="22" height="30" rx="3" stroke="#0284C7" strokeWidth="1.2" fill="rgba(2, 132, 199, 0.2)" />
            <rect x="49" y="28" width="40" height="12" rx="3" stroke="#38BDF8" strokeWidth="1.2" fill="rgba(56, 189, 248, 0.2)" />
            <rect x="49" y="44" width="40" height="14" rx="3" stroke="#BAE6FD" strokeWidth="1.2" />
          </g>
        </svg>
      ),
      deliverables: ['Figma Prototypes', 'Design Tokens', 'User Flow Maps'],
    },
    {
      num: '03',
      title: 'Agile Code Sprint',
      subtitle: 'Modular Engineering & Testing',
      desc: 'Sprint-based development using clean architecture, continuous commits, unit testing, and automated code quality checks.',
      color: '#818CF8',
      bgColor: 'rgba(129, 140, 248, 0.12)',
      borderColor: 'rgba(129, 140, 248, 0.35)',
      glowColor: 'rgba(129, 140, 248, 0.2)',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="16 18 22 12 16 6"></polyline>
          <polyline points="8 6 2 12 8 18"></polyline>
        </svg>
      ),
      // Top Right IDE / Code Matrix Graphic
      graphic: (
        <svg width="100" height="80" viewBox="0 0 100 80" fill="none">
          <g opacity="0.85">
            <rect x="10" y="12" width="80" height="56" rx="6" fill="rgba(15, 23, 42, 0.9)" stroke="#818CF8" strokeWidth="1.5" />
            <line x1="20" y1="26" x2="45" y2="26" stroke="#C084FC" strokeWidth="2" strokeLinecap="round" />
            <line x1="49" y1="26" x2="70" y2="26" stroke="#818CF8" strokeWidth="2" strokeLinecap="round" />
            <line x1="28" y1="36" x2="60" y2="36" stroke="#38BDF8" strokeWidth="2" strokeLinecap="round" />
            <line x1="28" y1="46" x2="75" y2="46" stroke="#34D399" strokeWidth="2" strokeLinecap="round" />
            <line x1="20" y1="56" x2="40" y2="56" stroke="#F472B6" strokeWidth="2" strokeLinecap="round" />
          </g>
        </svg>
      ),
      deliverables: ['Agile Sprints', 'Clean Code Architecture', 'Unit Test Suite'],
    },
    {
      num: '04',
      title: 'Automated QA & Security',
      subtitle: 'Vulnerability Scans & Penetration',
      desc: 'Comprehensive load testing, penetration testing, cross-browser audits, and SOC2/HIPAA compliance validation.',
      color: '#34D399',
      bgColor: 'rgba(52, 211, 153, 0.12)',
      borderColor: 'rgba(52, 211, 153, 0.35)',
      glowColor: 'rgba(52, 211, 153, 0.2)',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
          <polyline points="9 12 11 14 15 10"></polyline>
        </svg>
      ),
      // Top Right Security Radar Graphic
      graphic: (
        <svg width="100" height="80" viewBox="0 0 100 80" fill="none">
          <g opacity="0.85">
            <circle cx="50" cy="40" r="32" stroke="#34D399" strokeWidth="1" strokeDasharray="3 3" fill="none" />
            <circle cx="50" cy="40" r="22" stroke="#10B981" strokeWidth="1.5" fill="rgba(16, 185, 129, 0.1)" />
            <circle cx="50" cy="40" r="10" fill="rgba(52, 211, 153, 0.3)" stroke="#34D399" strokeWidth="1.5" />
            <path d="M50 18 L50 62 M28 40 L72 40" stroke="#34D399" strokeWidth="1" opacity="0.5" />
            <circle cx="50" cy="40" r="4" fill="#34D399" />
          </g>
        </svg>
      ),
      deliverables: ['Penetration Audits', 'Load Testing (100K RPS)', 'SOC2 Check'],
    },
    {
      num: '05',
      title: 'Zero-Downtime Launch',
      subtitle: 'Cloud Cluster & CI/CD Pipeline',
      desc: 'Automated Docker containerization, Kubernetes cluster orchestration, and zero-downtime blue/green deployment.',
      color: '#FBBF24',
      bgColor: 'rgba(251, 191, 36, 0.12)',
      borderColor: 'rgba(251, 191, 36, 0.35)',
      glowColor: 'rgba(251, 191, 36, 0.2)',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"></path>
          <polyline points="12 13 12 9 12 9"></polyline>
          <polyline points="9 11 12 8 15 11"></polyline>
        </svg>
      ),
      // Top Right 3D Cloud Server Graphic
      graphic: (
        <svg width="100" height="80" viewBox="0 0 100 80" fill="none">
          <g opacity="0.85">
            <path
              d="M68 32 A 16 16 0 0 0 38 24 A 20 20 0 0 0 20 44 A 14 14 0 0 0 34 58 L 70 58 A 14 14 0 0 0 68 32 Z"
              fill="rgba(245, 158, 11, 0.2)"
              stroke="#FBBF24"
              strokeWidth="1.8"
            />
            {/* Server Stack Boxes Below Cloud */}
            <rect x="40" y="44" width="30" height="10" rx="2" fill="#F59E0B" stroke="#FDE68A" strokeWidth="1" />
            <circle cx="45" cy="49" r="1.5" fill="#FFFFFF" />
            <rect x="40" y="58" width="30" height="10" rx="2" fill="#D97706" stroke="#FBBF24" strokeWidth="1" />
            <circle cx="45" cy="63" r="1.5" fill="#FFFFFF" />
          </g>
        </svg>
      ),
      deliverables: ['Kubernetes Manifests', 'CI/CD Pipelines', 'Blue/Green Launch'],
    },
    {
      num: '06',
      title: '24/7 SLA & Optimization',
      subtitle: 'Continuous Evolution & Monitoring',
      desc: 'Post-launch performance monitoring, automated alerts, SLA maintenance, and continuous feature enhancements.',
      color: '#F472B6',
      bgColor: 'rgba(244, 114, 182, 0.12)',
      borderColor: 'rgba(244, 114, 182, 0.35)',
      glowColor: 'rgba(244, 114, 182, 0.2)',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
          <line x1="8" y1="21" x2="16" y2="21"></line>
          <line x1="12" y1="17" x2="12" y2="21"></line>
        </svg>
      ),
      // Top Right Telemetry Speedometer & Bar Chart Graphic
      graphic: (
        <svg width="100" height="80" viewBox="0 0 100 80" fill="none">
          <g opacity="0.85">
            {/* Speed Gauge Arc */}
            <path
              d="M 25 50 A 25 25 0 0 1 75 50"
              fill="none"
              stroke="rgba(236, 72, 153, 0.3)"
              strokeWidth="4"
              strokeLinecap="round"
            />
            <path
              d="M 25 50 A 25 25 0 0 1 65 30"
              fill="none"
              stroke="#F472B6"
              strokeWidth="4"
              strokeLinecap="round"
            />
            <line x1="50" y1="50" x2="62" y2="34" stroke="#EC4899" strokeWidth="2.5" strokeLinecap="round" />
            <circle cx="50" cy="50" r="4" fill="#F472B6" />
            {/* Mini bar chart */}
            <rect x="75" y="38" width="4" height="14" fill="#EC4899" rx="1" />
            <rect x="82" y="32" width="4" height="20" fill="#F472B6" rx="1" />
            <rect x="89" y="26" width="4" height="26" fill="#FBCFE8" rx="1" />
          </g>
        </svg>
      ),
      deliverables: ['24/7 SLA Support', 'Real-time Telemetry', 'Feature Scaling'],
    },
  ];

  return (
    <section
      id="process"
      style={{
        padding: '60px 0 70px 0',
        position: 'relative',
        zIndex: 2,
        background: '#040612',
        color: '#FFFFFF',
        fontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      }}
    >
      {/* Background Ambient Radial Glows */}
      <div
        style={{
          position: 'absolute',
          top: '15%',
          left: '10%',
          width: '400px',
          height: '400px',
          background: 'radial-gradient(circle, rgba(168, 85, 247, 0.1) 0%, rgba(0,0,0,0) 70%)',
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: '15%',
          right: '10%',
          width: '400px',
          height: '400px',
          background: 'radial-gradient(circle, rgba(56, 189, 248, 0.1) 0%, rgba(0,0,0,0) 70%)',
          pointerEvents: 'none',
        }}
      />

      <div className="ht-container" style={{ maxWidth: '1240px', margin: '0 auto', padding: '0 20px' }}>
        
        {/* Section Header */}
        <div style={{ textAlign: 'center', marginBottom: '44px' }}>
          {/* Shield Tag Pill */}
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '6px 16px',
              borderRadius: '20px',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              fontSize: '0.72rem',
              fontWeight: 700,
              letterSpacing: '1.2px',
              color: '#D8B4FE',
              marginBottom: '16px',
              boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#C084FC" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
            </svg>
            <span>ENGINEERED FOR RELIABILITY</span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#C084FC" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
            </svg>
          </div>

          {/* Heading */}
          <h2
            style={{
              fontSize: 'clamp(2.1rem, 4vw, 3rem)',
              fontWeight: 800,
              letterSpacing: '-0.03em',
              margin: '0 0 12px 0',
              lineHeight: 1.15,
            }}
          >
            <span style={{ color: '#FFFFFF' }}>How We Deliver </span>
            <span
              style={{
                background: 'linear-gradient(90deg, #C084FC 0%, #38BDF8 50%, #00D4FF 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              World-Class Software
            </span>
          </h2>

          {/* Subtitle */}
          <p
            style={{
              color: '#94A3B8',
              fontSize: '0.96rem',
              maxWidth: '680px',
              margin: '0 auto',
              lineHeight: 1.5,
              fontWeight: 400,
            }}
          >
            A battle-tested 6-phase engineering roadmap that guarantees on-time delivery, zero-downtime deployment, and enterprise security.
          </p>
        </div>

        {/* 6-Phase Engineering Roadmap Grid (3x2) */}
        <div
          className="process-grid-layout"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '22px',
            marginBottom: '26px',
          }}
        >
          {steps.map((step, idx) => (
            <div
              key={idx}
              className="process-card-hover"
              style={{
                background: 'linear-gradient(145deg, rgba(14, 18, 38, 0.9) 0%, rgba(8, 11, 24, 0.95) 100%)',
                borderRadius: '20px',
                border: `1px solid ${step.borderColor}`,
                padding: '24px',
                boxShadow: `0 16px 40px rgba(0, 0, 0, 0.5), 0 0 22px ${step.glowColor}`,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              {/* Top Glow Accent Line */}
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '2px',
                  background: `linear-gradient(90deg, transparent 0%, ${step.color} 50%, transparent 100%)`,
                }}
              />

              <div>
                {/* Top Section: Icon, Phase Number & 3D Graphic */}
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {/* Theme Icon Rounded Square */}
                    <div
                      style={{
                        width: '44px',
                        height: '44px',
                        borderRadius: '12px',
                        background: step.bgColor,
                        border: `1px solid ${step.borderColor}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: step.color,
                        flexShrink: 0,
                        boxShadow: `0 0 16px ${step.glowColor}`,
                      }}
                    >
                      {step.icon}
                    </div>

                    <div>
                      <div
                        style={{
                          fontSize: '0.66rem',
                          fontWeight: 800,
                          color: step.color,
                          letterSpacing: '1.2px',
                          textTransform: 'uppercase',
                        }}
                      >
                        PHASE {step.num}
                      </div>
                      <div
                        style={{
                          fontSize: '2rem',
                          fontWeight: 900,
                          color: '#FFFFFF',
                          lineHeight: 1,
                          fontFamily: "'Plus Jakarta Sans', sans-serif",
                          letterSpacing: '-0.03em',
                        }}
                      >
                        {step.num}
                      </div>
                    </div>
                  </div>

                  {/* Top Right High-Tech Graphic */}
                  <div style={{ pointerEvents: 'none' }}>
                    {step.graphic}
                  </div>
                </div>

                {/* Title & Subtitle */}
                <h3 style={{ fontSize: '1.28rem', fontWeight: 800, color: '#FFFFFF', margin: '0 0 4px 0', lineHeight: 1.2 }}>
                  {step.title}
                </h3>
                <div style={{ fontSize: '0.76rem', fontWeight: 700, color: step.color, marginBottom: '12px' }}>
                  {step.subtitle}
                </div>

                {/* Description */}
                <p style={{ fontSize: '0.8rem', color: '#94A3B8', lineHeight: 1.5, margin: '0 0 18px 0' }}>
                  {step.desc}
                </p>
              </div>

              {/* Key Deliverables Section */}
              <div>
                <div
                  style={{
                    fontSize: '0.62rem',
                    fontWeight: 800,
                    color: '#64748B',
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    marginBottom: '8px',
                  }}
                >
                  KEY DELIVERABLES
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {step.deliverables.map((deliv, i) => (
                    <span
                      key={i}
                      style={{
                        padding: '4px 10px',
                        borderRadius: '8px',
                        background: 'rgba(255, 255, 255, 0.04)',
                        border: `1px solid ${step.borderColor}`,
                        color: '#E2E8F0',
                        fontSize: '0.72rem',
                        fontWeight: 600,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '5px',
                      }}
                    >
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={step.color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      {deliv}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ======================================================== */}
        {/* BOTTOM STATS BANNER */}
        {/* ======================================================== */}
        <div
          className="bottom-roadmap-banner"
          style={{
            background: 'linear-gradient(145deg, rgba(13, 17, 38, 0.75) 0%, rgba(8, 11, 24, 0.85) 100%)',
            borderRadius: '18px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            padding: '18px 24px',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.4)',
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '16px',
            alignItems: 'center',
          }}
        >
          {/* Item 1: Process */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div
              style={{
                width: '42px',
                height: '42px',
                borderRadius: '50%',
                background: 'rgba(168, 85, 247, 0.15)',
                border: '1px solid rgba(168, 85, 247, 0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#C084FC',
                flexShrink: 0,
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.71.79-1.81.79-1.81l-3-3s-1.1.08-1.79.79z"></path>
                <path d="M15 9l-6 6"></path>
                <path d="M9 15l-3-3 7.64-7.64a2.76 2.76 0 0 1 3.9 3.9L9.9 15.9z"></path>
              </svg>
            </div>
            <div>
              <div style={{ fontSize: '0.92rem', fontWeight: 800, color: '#FFFFFF', lineHeight: 1.2 }}>
                Battle-Tested Process
              </div>
              <div style={{ fontSize: '0.72rem', color: '#94A3B8', marginTop: '2px' }}>Proven across 100+ enterprise projects</div>
            </div>
          </div>

          {/* Item 2: Delivery */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div
              style={{
                width: '42px',
                height: '42px',
                borderRadius: '50%',
                background: 'rgba(56, 189, 248, 0.15)',
                border: '1px solid rgba(56, 189, 248, 0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#38BDF8',
                flexShrink: 0,
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
            </div>
            <div>
              <div style={{ fontSize: '0.92rem', fontWeight: 800, color: '#FFFFFF', lineHeight: 1.2 }}>
                On-Time Delivery
              </div>
              <div style={{ fontSize: '0.72rem', color: '#94A3B8', marginTop: '2px' }}>95% projects delivered on schedule</div>
            </div>
          </div>

          {/* Item 3: Security */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div
              style={{
                width: '42px',
                height: '42px',
                borderRadius: '50%',
                background: 'rgba(52, 211, 153, 0.15)',
                border: '1px solid rgba(52, 211, 153, 0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#34D399',
                flexShrink: 0,
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
              </svg>
            </div>
            <div>
              <div style={{ fontSize: '0.92rem', fontWeight: 800, color: '#FFFFFF', lineHeight: 1.2 }}>
                Enterprise Security
              </div>
              <div style={{ fontSize: '0.72rem', color: '#94A3B8', marginTop: '2px' }}>Security-first at every phase</div>
            </div>
          </div>

          {/* Item 4: Continuous Improvement */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div
              style={{
                width: '42px',
                height: '42px',
                borderRadius: '50%',
                background: 'rgba(244, 114, 182, 0.15)',
                border: '1px solid rgba(244, 114, 182, 0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#F472B6',
                flexShrink: 0,
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
                <polyline points="17 6 23 6 23 12"></polyline>
              </svg>
            </div>
            <div>
              <div style={{ fontSize: '0.92rem', fontWeight: 800, color: '#FFFFFF', lineHeight: 1.2 }}>
                Continuous Improvement
              </div>
              <div style={{ fontSize: '0.72rem', color: '#94A3B8', marginTop: '2px' }}>Always optimizing for better</div>
            </div>
          </div>
        </div>

      </div>

      {/* Embedded CSS for responsive scaling */}
      <style>{`
        .process-card-hover {
          transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
        }
        .process-card-hover:hover {
          transform: translateY(-4px);
        }
        @media (max-width: 1024px) {
          .process-grid-layout {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .bottom-roadmap-banner {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 20px !important;
          }
        }
        @media (max-width: 640px) {
          .process-grid-layout {
            grid-template-columns: 1fr !important;
          }
          .bottom-roadmap-banner {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </section>
  );
};

export default ProcessTimeline;
